import { addWidgetToolOutputSchema, queryHelpCenterToolName, queryHelpCenterToolOutputSchema } from '../externalTools';
import type { UIMessage } from 'ai';

export type ChatMessagePart = UIMessage['parts'][number];

const getMessageText = (parts: readonly ChatMessagePart[]) =>
    parts
        .filter((p): p is { type: 'text'; text: string } => p.type === 'text' && typeof p.text === 'string')
        .map((p) => p.text)
        .join('');

const getToolName = (part: Extract<ChatMessagePart, { type: `tool-${string}` | 'dynamic-tool' }>) =>
    part.type === 'dynamic-tool' ? part.toolName : part.type.slice('tool-'.length);

const isToolMessagePart = (
    part: ChatMessagePart,
): part is Extract<ChatMessagePart, { type: `tool-${string}` | 'dynamic-tool' }> =>
    part.type === 'dynamic-tool' || part.type.startsWith('tool-');

const getToolResultText = (toolName: string, output: unknown) => {
    if (toolName === queryHelpCenterToolName) {
        const parsed = queryHelpCenterToolOutputSchema.safeParse(output);
        if (!parsed.success) {
            return 'Searched the help center.';
        }

        if (!parsed.data.success) {
            return parsed.data.error
                ? `Could not search the help center: ${parsed.data.error}`
                : 'Could not search the help center.';
        }

        const resultCount = parsed.data.results.length;
        if (resultCount === 0) {
            return 'No matching help center content found.';
        }
        return resultCount === 1 ? 'Found 1 help center result.' : `Found ${resultCount} help center results.`;
    }

    if (toolName !== 'addWidget') {
        return `Completed ${toolName}.`;
    }

    const parsed = addWidgetToolOutputSchema.safeParse(output);
    if (!parsed.success) {
        return 'Completed the requested widget action.';
    }

    if (!parsed.data.success) {
        return parsed.data.error
            ? `Could not open the requested widget: ${parsed.data.error}`
            : 'Could not open the requested widget.';
    }

    return parsed.data.widgetType ? `Opened the ${parsed.data.widgetType} widget.` : 'Opened the requested widget.';
};

const getToolPartText = (part: ChatMessagePart): string | null => {
    if (!isToolMessagePart(part)) {
        return null;
    }

    const toolName = getToolName(part);

    switch (part.state) {
        case 'input-streaming':
        case 'input-available':
            return `Working on ${toolName}...`;

        case 'approval-requested':
            return `The assistant needs approval to continue ${toolName}.`;

        case 'approval-responded':
            return part.approval.approved
                ? `Continuing ${toolName} after approval.`
                : `Stopped ${toolName} because approval was denied.`;

        case 'output-available':
            return getToolResultText(toolName, part.output);

        case 'output-error':
            return part.errorText ? `${toolName} failed: ${part.errorText}` : `${toolName} failed.`;

        case 'output-denied':
            return `${toolName} was denied.`;
    }
};

const getSourceSummary = (parts: readonly ChatMessagePart[]) => {
    const sourceCount = parts.filter((part) => part.type === 'source-url' || part.type === 'source-document').length;

    if (sourceCount === 0) {
        return '';
    }

    return sourceCount === 1 ? 'Shared 1 supporting source.' : `Shared ${sourceCount} supporting sources.`;
};

export const getRenderableMessageText = (parts: readonly ChatMessagePart[]) => {
    const text = getMessageText(parts);
    if (text.trim()) {
        return text;
    }

    const toolText = parts
        .map(getToolPartText)
        .filter((partText): partText is string => Boolean(partText))
        .join('\n\n');
    if (toolText) {
        return toolText;
    }

    const sourceSummary = getSourceSummary(parts);
    if (sourceSummary) {
        return sourceSummary;
    }

    return parts.some((part) => part.type !== 'step-start') ? 'The assistant responded without a text reply.' : '';
};

export const parseAssistantErrorMessage = (error: Error | undefined) => {
    if (!error?.message) return null;

    const raw = error.message;
    try {
        const parsed: unknown = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            if ('message' in parsed && typeof parsed.message === 'string' && parsed.message.trim()) {
                return parsed.message;
            }
            if ('error' in parsed && typeof parsed.error === 'string' && parsed.error.trim()) {
                return parsed.error;
            }
        }
    } catch {
        // The AI SDK often exposes raw response bodies in Error.message.
    }

    return raw;
};
